// All Workspaces Modal Component for browsing and managing workspaces

import React, { useState, useMemo } from 'react';
import { ModalCard } from '@/components/ui';
import { useWorkspaces, useDeleteWorkspace } from '@/hooks/useWorkspaces';
import { usePermissions } from '@/hooks/usePermissions';
import { WorkspaceListItem } from '@/types/workspace';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  UserIcon,
  CheckBadgeIcon,
  ArrowDownCircleIcon,
  ChartBarIcon,
  TagIcon,
  Squares2X2Icon,
  ListBulletIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';

interface AllWorkspacesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onWorkspaceClick: (workspace: { id: string; name: string; version: number }) => void;
}

type ViewMode = 'grid' | 'list';

const AllWorkspacesModal: React.FC<AllWorkspacesModalProps> = ({
  isOpen,
  onClose,
  onWorkspaceClick,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'updatedAt' | 'createdAt'>('updatedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [dataFreshnessFilter, setDataFreshnessFilter] = useState<'all' | 'latest' | 'historical'>('all');
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<WorkspaceListItem | null>(null);

  // Fetch workspaces with current filters
  const {
    data: workspacesData,
    isLoading,
    error,
  } = useWorkspaces({
    search: searchTerm,
    tags: selectedTags,
    sortBy,
    sortOrder,
    size: 50, // Show more workspaces in modal
  });

  // Delete workspace mutation
  const deleteWorkspaceMutation = useDeleteWorkspace();
  
  // Permissions
  const permissions = usePermissions();

  // Filter by data freshness locally since API might not support this filter
  const filteredWorkspaces = useMemo(() => {
    if (!workspacesData?.workspaces) return [];
    
    let filtered = workspacesData.workspaces;
    
    if (dataFreshnessFilter !== 'all') {
      filtered = filtered.filter((workspace) => {
        if (dataFreshnessFilter === 'latest') return workspace.isLatestData;
        if (dataFreshnessFilter === 'historical') return !workspace.isLatestData;
        return true;
      });
    }
    
    return filtered;
  }, [workspacesData, dataFreshnessFilter]);

  // Get all unique tags for filtering
  const allTags = useMemo(() => {
    if (!workspacesData?.workspaces) return [];
    const tagSet = new Set<string>();
    workspacesData.workspaces.forEach((workspace) => {
      workspace.tags?.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [workspacesData]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedTags([]);
    setDataFreshnessFilter('all');
  };

  // Delete handlers
  const handleDeleteWorkspace = (workspace: WorkspaceListItem) => {
    setWorkspaceToDelete(workspace);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!workspaceToDelete) return;
    
    try {
      await deleteWorkspaceMutation.mutateAsync(workspaceToDelete.id);
      setShowDeleteConfirm(false);
      setWorkspaceToDelete(null);
    } catch (error) {
      console.error('Failed to delete workspace:', error);
      // Error is handled by the mutation's onError
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const renderWorkspaceCard = (workspace: WorkspaceListItem) => {
    const sampleSize = workspace.metadata?.totalFailures || 0;
    
    return (
      <div
        key={workspace.id}
        className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer relative"
        onClick={() => onWorkspaceClick({
          id: workspace.id,
          name: workspace.name,
          version: workspace.version,
        })}
      >
        {/* Action buttons */}
        <div className="absolute top-2 right-2 flex space-x-1">
          {permissions.canEditWorkspace(workspace) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                // TODO: Open edit modal or navigate to edit page
              }}
              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
              title={permissions.isWorkspaceOwner(workspace) ? "Edit your workspace" : "Edit workspace"}
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
          {permissions.canDeleteWorkspace(workspace) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteWorkspace(workspace);
              }}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
              title="Delete workspace"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="font-medium text-gray-900 truncate">{workspace.name}</h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                v{workspace.version}
              </span>
              {/* Data freshness indicator using icon */}
              <div className="group relative">
                {workspace.isLatestData ? (
                  <CheckBadgeIcon className="w-4 h-4 text-green-600" />
                ) : (
                  <ArrowDownCircleIcon className="w-4 h-4 text-amber-600" />
                )}
                <div className="absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {workspace.isLatestData
                    ? 'Uses latest dataset'
                    : 'Uses historical data'}
                </div>
              </div>
            </div>
            
            {workspace.description && (
              <p className="text-sm text-gray-600 mb-2 line-clamp-2">{workspace.description}</p>
            )}
            
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <ChartBarIcon className="w-3 h-3" />
                <span>{sampleSize} samples</span>
              </div>
              
              {workspace.metadata.componentsAnalyzed.length > 0 && (
                <div className="flex items-center space-x-1">
                  <span>
                    {workspace.metadata.componentsAnalyzed.length > 2
                      ? `${workspace.metadata.componentsAnalyzed.slice(0, 2).join(', ')} +${workspace.metadata.componentsAnalyzed.length - 2}`
                      : workspace.metadata.componentsAnalyzed.join(', ')}
                  </span>
                </div>
              )}
              
              {workspace.createdBy && (
                <div className="flex items-center space-x-1">
                  <UserIcon className="w-3 h-3" />
                  <span>by {workspace.createdBy.name}</span>
                </div>
              )}
            </div>
            
            <div className="mt-2 text-xs text-gray-400">
              <span>Created {formatDate(workspace.createdAt)}</span>
              <span className="mx-2">•</span>
              <span>Updated {formatDate(workspace.updatedAt)}</span>
            </div>
            
            {/* Tags */}
            {workspace.tags && workspace.tags.length > 0 && (
              <div className="flex items-center space-x-1 mt-2">
                <TagIcon className="w-3 h-3 text-gray-400" />
                <div className="flex space-x-1">
                  {workspace.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                  {workspace.tags.length > 3 && (
                    <span className="text-gray-400 text-xs">
                      +{workspace.tags.length - 3}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderWorkspaceList = (workspace: WorkspaceListItem) => {
    const sampleSize = workspace.metadata?.totalFailures || 0;
    
    return (
      <div
        key={workspace.id}
        className="bg-white border border-gray-200 rounded-lg p-3 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer relative"
        onClick={() => onWorkspaceClick({
          id: workspace.id,
          name: workspace.name,
          version: workspace.version,
        })}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              {workspace.isLatestData ? (
                <CheckBadgeIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
              ) : (
                <ArrowDownCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0" />
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="font-medium text-gray-900 truncate">{workspace.name}</h3>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  v{workspace.version}
                </span>
              </div>
              
              <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                <span>{sampleSize} samples</span>
                <span>Updated {formatDate(workspace.updatedAt)}</span>
                {workspace.createdBy && (
                  <div className="flex items-center space-x-1">
                    <UserIcon className="w-3 h-3" />
                    <span>{workspace.createdBy.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex space-x-1">
            {permissions.canEditWorkspace(workspace) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // TODO: Open edit modal or navigate to edit page
                }}
                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                title={permissions.isWorkspaceOwner(workspace) ? "Edit your workspace" : "Edit workspace"}
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            )}
            {permissions.canDeleteWorkspace(workspace) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteWorkspace(workspace);
                }}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete workspace"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <ModalCard
        isOpen={isOpen}
        onClose={onClose}
        title="All Workspaces"
        subtitle="Browse and manage your Weibull analysis workspaces"
        size="xl"
        closeOnBackdrop={false}
      >
        <div className="max-h-[80vh] flex flex-col">
          {/* Search and Filter Bar */}
          <div className="p-6 border-b border-gray-200 flex-shrink-0">            {/* Search Input */}
            <div className="relative mb-4">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search workspaces by name or description..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue"
              />
            </div>

            {/* Filter and Sort Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {/* Filters Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center space-x-2 px-3 py-2 border rounded-lg transition-colors ${
                    showFilters || selectedTags.length > 0 || dataFreshnessFilter !== 'all'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FunnelIcon className="w-4 h-4" />
                  <span>Filters</span>
                  {(selectedTags.length > 0 || dataFreshnessFilter !== 'all') && (
                    <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {selectedTags.length + (dataFreshnessFilter !== 'all' ? 1 : 0)}
                    </span>
                  )}
                </button>

                {/* Data Freshness Filter */}
                <select
                  value={dataFreshnessFilter}
                  onChange={(e) => setDataFreshnessFilter(e.target.value as 'all' | 'latest' | 'historical')}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue"
                >
                  <option value="all">All Data</option>
                  <option value="latest">Latest Data Only</option>
                  <option value="historical">Historical Data Only</option>
                </select>

                {/* Sort Options */}
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as 'name' | 'updatedAt' | 'createdAt');
                    setSortOrder(order as 'asc' | 'desc');
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue"
                >
                  <option value="updatedAt-desc">Recently Modified</option>
                  <option value="createdAt-desc">Recently Created</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <Squares2X2Icon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <ListBulletIcon className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="bg-gray-50 rounded-lg p-4 mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700">Filter by Tags</h4>
                  {(selectedTags.length > 0 || dataFreshnessFilter !== 'all') && (
                    <button
                      onClick={clearFilters}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {allTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {allTags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => handleTagToggle(tag)}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                          selectedTags.includes(tag)
                            ? 'bg-blue-500 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No tags found in your workspaces</p>
                )}
              </div>
            )}
          </div>

          {/* Workspace Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-onyx-bright-blue"></div>
                <span className="ml-2 text-gray-600">Loading workspaces...</span>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <div className="text-red-600 mb-2">Failed to load workspaces</div>
                <button
                  onClick={() => window.location.reload()}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Workspace List */}
            {filteredWorkspaces && (
              <>
                {filteredWorkspaces.length > 0 ? (
                  <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-3'}>
                    {filteredWorkspaces.map((workspace) =>
                      viewMode === 'grid' ? renderWorkspaceCard(workspace) : renderWorkspaceList(workspace)
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-gray-500 mb-2">
                      {searchTerm || selectedTags.length > 0 || dataFreshnessFilter !== 'all'
                        ? 'No workspaces match your filters'
                        : 'No saved workspaces found'}
                    </div>
                    {(searchTerm || selectedTags.length > 0 || dataFreshnessFilter !== 'all') && (
                      <button
                        onClick={clearFilters}
                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer Info */}
          {filteredWorkspaces && filteredWorkspaces.length > 0 && (
            <div className="px-6 py-3 border-t border-gray-200 text-sm text-gray-500 flex-shrink-0">
              Showing {filteredWorkspaces.length} of {workspacesData?.total || 0} workspaces
            </div>
          )}
        </div>
      </ModalCard>

      {/* Single Delete Confirmation Modal */}
      {showDeleteConfirm && workspaceToDelete && (
        <ModalCard
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Delete Workspace"
          size="sm"
        >
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <p className="text-gray-900">
                  Are you sure you want to delete &quot;{workspaceToDelete.name}&quot;?
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  This action cannot be undone. All version history will be permanently lost.
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                disabled={deleteWorkspaceMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteWorkspaceMutation.isPending}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteWorkspaceMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </ModalCard>
      )}
    </>
  );
};

export default AllWorkspacesModal;
